import React, { useEffect, useMemo, useState } from "react"

import { useClipboard } from "use-clipboard-copy"

import { PrimaryText } from "@front-shared/elements/primaryText.element"
import { TooltipElement } from "@front-shared/elements/tooltip.element"

import checkIcon from "@front-shared/assets/images/icons/check-dark3.svg"
import copyIcon from "@front-shared/assets/images/icons/copy-dark.svg"

export const CoverageInputDataDifferenceComponent: React.FunctionComponent<
    ICoverageInputDataDifferenceComponentProps
> = props => {
    const clipboard = useClipboard()
    const [isCopied, setIsCopied] = useState<boolean>(false)

    const isOverriddenDataDifferent = useMemo<boolean>(() => {
        return (
            !!props.initialData &&
            !!props.overridenData &&
            props.initialData.toLowerCase().trim() !==
                props.overridenData.toLowerCase().trim()
        )
    }, [props.initialData, props.overridenData])

    useEffect(() => {
        setIsCopied(clipboard.copied)
    }, [clipboard.copied])

    useEffect(() => {
        if (!isCopied) {
            return
        }

        const timeoutId = setTimeout(() => {
            setIsCopied(false)
        }, 3 * 1000)

        return () => {
            try {
                clearTimeout(timeoutId)
            } catch (e) {}
        }
    }, [isCopied])

    const getHighlightedTextDifference = (
        overridenData: string,
        initialData?: string,
    ): JSX.Element => {
        return (
            <>
                {overridenData.split("").map((newChar, index) => {
                    return (
                        <span
                            key={index}
                            className={
                                !initialData ||
                                newChar.toLowerCase() !==
                                    initialData.charAt(index)?.toLowerCase()
                                    ? "text-semibold"
                                    : undefined
                            }
                        >
                            {newChar}
                        </span>
                    )
                })}
            </>
        )
    }

    return (
        <div className={`relative ${props.className}`}>
            <PrimaryText
                typography="text"
                className={
                    isOverriddenDataDifferent
                        ? `
                            flex items-center
                            pt-[4px] relative left-[-8px] bg-brand-white
                            px-[8px] rounded-[4px] border border-solid border-brand-black-transparent-010
                        `
                        : ""
                }
            >
                <div className="flex-1 mouseflow-ignore">
                    {isOverriddenDataDifferent && props.overridenData
                        ? getHighlightedTextDifference(
                              props.overridenData,
                              props.initialData,
                          )
                        : props.initialData}
                </div>

                {isOverriddenDataDifferent && props.overridenData && (
                    <>
                        <img
                            className="inline-block cursor-pointer ml-[16px]"
                            src={isCopied ? checkIcon : copyIcon}
                            alt="Copy"
                            onClick={() => {
                                clipboard.copy(props.overridenData)
                            }}
                        />

                        {props.initialData && (
                            <TooltipElement
                                className="ml-[4px]"
                                text={`You entered: <u>${props.initialData}</u>`}
                            />
                        )}
                    </>
                )}
            </PrimaryText>
        </div>
    )
}

interface ICoverageInputDataDifferenceComponentProps {
    className?: string
    initialData?: string
    overridenData?: string
}
